import { useCallback, useRef, useState, useEffect } from 'react';
import '../Estudios';
import Popup from 'devextreme-react/popup';
import { useApiConfiguration } from '../../../api/api.configuration';
import { useScreenSize } from '../../../utils/media-query';
import DataGrid, { Column, Button as Butt, Paging, Scrolling, Selection, FilterRow, RowDragging, SearchPanel } from 'devextreme-react/data-grid';
import DropDownBox, { DropDownBoxTypes } from 'devextreme-react/drop-down-box';
import { LoadPanel } from 'devextreme-react/load-panel';
import { SysIAnalitos } from '../../../types/types.analitos';
import { Rpm_EstudiosAnalitos } from '../../../types/types.estudios';
import { confirm } from 'devextreme/ui/dialog';
import { newToast } from '../../../components';

export const ModalAnalytesByStudy = ({ visible, setVisible, IdEstudio, Estudio, Analitos }: { visible: boolean; setVisible: (value: boolean) => void; IdEstudio: number; Estudio: string; Analitos: SysIAnalitos[] }) => {
  const { getAnalytesByStudy, newStudyAnalyte, setStudyAnalyte, deleteStudyAnalyte } = useApiConfiguration();
  const { isMedium, isSmall } = useScreenSize();
  const [gridBoxValue, setGridBoxValue] = useState([2]);
  const [isGridBoxOpened, setIsGridBoxOpened] = useState(false);
  const [studyAnalytes, setStudyAnalytes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const ownerLabel = { 'aria-label': 'Owner' };
  const dataRef = useRef<DataGrid>(null);
  const dataRefs = useRef<DataGrid>(null);
  const isSavingRef = useRef(false);

  // Ref para guardar los órdenes originales del servidor y detectar qué cambió
  const serverOrdersRef = useRef<Map<number, number>>(new Map());

  // Ref estable para las funciones del API (evita re-renders infinitos)
  const apiRef = useRef({ getAnalytesByStudy, setStudyAnalyte, deleteStudyAnalyte, newStudyAnalyte });
  apiRef.current = { getAnalytesByStudy, setStudyAnalyte, deleteStudyAnalyte, newStudyAnalyte };

  // Cargar datos del estudio y normalizar órdenes localmente
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await apiRef.current.getAnalytesByStudy(IdEstudio);
      if (!Array.isArray(res)) {
        setStudyAnalytes([]);
        serverOrdersRef.current = new Map();
        return;
      }

      const sorted = [...res].sort((a, b) => (a.Rea_OrdenImpresion ?? Number.MAX_SAFE_INTEGER) - (b.Rea_OrdenImpresion ?? Number.MAX_SAFE_INTEGER));

      // Guardar órdenes originales del servidor
      const serverMap = new Map<number, number>();
      sorted.forEach((item) => serverMap.set(item.Rea_Id, item.Rea_OrdenImpresion));
      serverOrdersRef.current = serverMap;

      // Normalizar a 1..n para mostrar siempre órdenes limpios
      const normalized = sorted.map((item, idx) => ({
        ...item,
        Rea_OrdenImpresion: idx + 1,
      }));

      setStudyAnalytes(normalized);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setStudyAnalytes([]);
    } finally {
      setIsLoading(false);
    }
  }, [IdEstudio]);

  // Cargar al abrir el popup
  useEffect(() => {
    if (visible && IdEstudio) {
      loadData();
    }
  }, [visible, IdEstudio, loadData]);

  // Persistir cambios de orden SECUENCIALMENTE (evita colapsar el backend)
  const persistOrderChanges = useCallback(async (items: any[]) => {
    if (isSavingRef.current) return; // Evitar saves concurrentes

    const toUpdate = items.filter((item) => {
      const serverOrder = serverOrdersRef.current.get(item.Rea_Id);
      return serverOrder !== item.Rea_OrdenImpresion;
    });

    if (toUpdate.length === 0) return;

    isSavingRef.current = true;
    setIsLoading(true);

    try {
      // Enviar uno por uno para no colapsar el backend
      for (const item of toUpdate) {
        await apiRef.current.setStudyAnalyte({
          RpmAnalito: { Rea_OrdenImpresion: item.Rea_OrdenImpresion },
          IdRpm: item.Rea_Id,
        });
      }

      // Actualizar ref con los nuevos valores persistidos
      items.forEach((item) => serverOrdersRef.current.set(item.Rea_Id, item.Rea_OrdenImpresion));
    } catch (error) {
      console.error('Error al persistir orden:', error);
      newToast('ERROR', 'Error al guardar el orden', 3);
    } finally {
      isSavingRef.current = false;
      setIsLoading(false);
    }
  }, []);

  // Manejar reordenamiento por arrastre (drag & drop)
  const onReorder = useCallback(
    async (e: any) => {
      const fromIndex = e.fromIndex;
      const toIndex = e.toIndex;

      if (fromIndex === toIndex) return;

      const reordered = [...studyAnalytes];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);

      // Reasignar órdenes consecutivos 1..n
      const updated = reordered.map((item, idx) => ({
        ...item,
        Rea_OrdenImpresion: idx + 1,
      }));

      // Actualizar UI inmediatamente
      setStudyAnalytes(updated);

      // Persistir sólo lo que cambió respecto al servidor
      await persistOrderChanges(updated);
      newToast('SUCCESS', 'Orden actualizado', 2);
    },
    [studyAnalytes, persistOrderChanges]
  );

  // Normalizar órdenes manualmente
  const normalizeOrders = useCallback(async () => {
    const normalized = studyAnalytes.map((item, idx) => ({
      ...item,
      Rea_OrdenImpresion: idx + 1,
    }));

    const hasChanges = normalized.some((item) => {
      const serverOrder = serverOrdersRef.current.get(item.Rea_Id);
      return serverOrder !== item.Rea_OrdenImpresion;
    });

    if (!hasChanges) {
      newToast('WARNING', 'El orden ya está normalizado', 2);
      return;
    }

    setStudyAnalytes(normalized);
    await persistOrderChanges(normalized);
    newToast('SUCCESS', 'Orden normalizado correctamente', 2);
  }, [studyAnalytes, persistOrderChanges]);

  // Eliminar analito con confirmación
  const handleDeleteAnalyte = useCallback(
    async (e: any) => {
      const reaId = e.row?.data?.Rea_Id;
      const nombre = e.row?.data?.Analito?.An_Nombre || 'este analito';
      if (!reaId) return;

      const result = await confirm(`¿Eliminar "${nombre}" del estudio?`, 'Confirmar eliminación');
      if (result) {
        const res = await apiRef.current.deleteStudyAnalyte({ IdRpm: reaId });
        if (res === 107) {
          await loadData();
        }
      }
    },
    [loadData]
  );

  const handleVisible = useCallback(() => {
    setVisible(false);
  }, []);

  const dataGridOnSelectionChanged = useCallback((e: { selectedRowKeys: any }) => {
    setGridBoxValue(e.selectedRowKeys);
    setIsGridBoxOpened(false);
  }, []);

  const handleConfigureClick = useCallback(
    (e) => {
      dataRefs.current?.instance.refresh();
      const { data }: { data: SysIAnalitos } = e.row;
      if (data) {
        const maxOrder = studyAnalytes.length > 0 ? Math.max(...studyAnalytes.map((a) => a.Rea_OrdenImpresion || 0)) : 0;
        const updatedDataRpm: Rpm_EstudiosAnalitos = {
          Rea_Id: 0,
          Rea_OrdenImpresion: maxOrder + 1,
          Rea_An_Id: data.An_Id,
          Rea_Es_Id: IdEstudio,
        };
        apiRef.current.newStudyAnalyte({ RpmAnalito: updatedDataRpm }).then((res) => {
          if (res === 105) {
            loadData();
          }
        });
        setIsGridBoxOpened(false);
      }
    },
    [studyAnalytes, IdEstudio, loadData]
  );

  const dataGridRender = useCallback(() => {
    return (
      <div style={{ height: 300 }}>
        <DataGrid
          dataSource={Analitos?.length ? Analitos : []}
          ref={dataRefs}
          hoverStateEnabled={true}
          showBorders={true}
          selectedRowKeys={gridBoxValue}
          onSelectionChanged={dataGridOnSelectionChanged}
          height='100%' // ✅ tomará 300px del contenedor padre
        >
          <Scrolling mode='virtual' />
          <Selection mode='single' />
          <Paging enabled={true} />
          <FilterRow visible={true} />
          <Column name='An_Id' caption='ID' dataField='An_Id' width={100} alignment='center' />
          <Column name='An_Nombre' caption='ANALITO' dataField='An_Nombre' alignment='left' />
          <Column type='buttons' caption='ACCIÓN'>
            <Butt hint='Configurar' icon='plus' visible={true} disabled={false} onClick={handleConfigureClick} />
          </Column>
        </DataGrid>
      </div>
    );
  }, [Analitos, gridBoxValue, dataGridOnSelectionChanged]);

  const gridBoxDisplayExpr = (item: { An_Id: any; An_Nombre: any }) => item && `${item.An_Nombre} <${item.An_Id}>`;

  const handleSelectedData = useCallback((e: DropDownBoxTypes.ValueChangedEvent) => {
    setGridBoxValue(e.value);
  }, []);

  const onGridBoxOpened = useCallback((e: DropDownBoxTypes.OptionChangedEvent) => {
    if (e.name === 'opened') {
      setIsGridBoxOpened(e.value);
    }
  }, []);

  return (
    <Popup width={1200} height={'auto'} fullScreen={isMedium || isSmall} visible={visible} onHidden={handleVisible} title={IdEstudio + ' - ' + Estudio} showTitle={true} showCloseButton={true}>
      <div style={{ position: 'relative' }}>
        <LoadPanel visible={isLoading} message='Procesando...' showPane={true} shading={true} shadingColor='rgba(0, 0, 0, 0.2)' position={{ of: '.grid' }} />
        <DropDownBox dataSource={Analitos || []} value={gridBoxValue} opened={isGridBoxOpened} valueExpr={'An_Id'} displayExpr={gridBoxDisplayExpr} deferRendering={false} inputAttr={ownerLabel} placeholder='ANALITOS' showClearButton={true} onValueChanged={handleSelectedData} onOptionChanged={onGridBoxOpened} contentRender={dataGridRender} />
        <br />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px', gap: '8px' }}>
          <span
            onClick={normalizeOrders}
            style={{
              padding: '4px 10px',
              fontSize: '12px',
              color: '#1976d2',
              cursor: 'pointer',
              borderRadius: '4px',
              border: '1px solid #bbdefb',
              backgroundColor: '#e3f2fd',
              userSelect: 'none',
            }}
            title='Reasignar órdenes consecutivos desde 1 y guardar'>
            🔄 Normalizar orden
          </span>
        </div>
        <DataGrid className='grid' dataSource={studyAnalytes} keyExpr='Rea_Id' ref={dataRef} hoverStateEnabled={true} height={450} rowAlternationEnabled={true} showColumnLines={true} showBorders={true}>
          <SearchPanel visible={true} placeholder='Buscar analito...' />
          <RowDragging allowReordering={true} onReorder={onReorder} showDragIcons={true} dropFeedbackMode='push' autoScroll={true} scrollSpeed={30} scrollSensitivity={60} />
          <Scrolling mode='standard' />
          <Paging enabled={false} />
          <Column allowGrouping={false} allowSorting={false} caption='ID' visible={true} dataField='Analito.An_Id' width={80} alignment='center' name='An_Id' />
          <Column allowGrouping={false} caption='ANALITO' visible={true} dataField='Analito.An_Nombre' alignment='left' />
          <Column allowGrouping={false} caption='ABREVIATURA' allowSorting={false} dataField='Analito.An_Abreviatura' width={120} alignment='center' />
          <Column allowGrouping={false} caption='ORDEN' allowSorting={false} dataField='Rea_OrdenImpresion' width={80} alignment='center' />
          <Column type='buttons' caption={'ACCIÓN'} width={100}>
            <Butt hint='Eliminar' icon='trash' onClick={handleDeleteAnalyte} />
          </Column>
        </DataGrid>
      </div>
    </Popup>
  );
};
